// ── BeeLove Scout ─────────────────────────────────────────────────────────
import { useState, useMemo, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { SCOUT_TYPES, formatScoutDate, formatDeadlineCountdown, saveScoutPrefs, loadScoutPrefs } from '../utils/scoutAlgo';
import CreateScoutSheet from '../components/CreateScoutSheet';

// ── Design tokens (match app globals) ────────────────────────────────────
const G = {
  bg:'var(--black)', card:'var(--dark)', border:'var(--border)',
  white:'var(--white)', muted:'var(--muted)', gold:'var(--gold)',
  goldPale:'var(--gold-pale)', goldBdr:'var(--gold-border)',
  green:'#4EC994', red:'#E07070', blue:'#5B9BD5', purple:'#A084C9',
  sans:"'Outfit',sans-serif", serif:"'Cormorant Garamond',serif",
};

// ── Small helpers ─────────────────────────────────────────────────────────
function InterestTag({ interest }) {
  const label = interest.replace('interest.', '').replace(/_/g, ' ');
  return (
    <span style={{ fontSize:'0.65rem', padding:'0.15rem 0.5rem', background:G.goldPale, border:`1px solid ${G.goldBdr}`, borderRadius:99, color:G.gold, textTransform:'capitalize' }}>
      {label}
    </span>
  );
}

function MemberAvatar({ member, size = 36, showName = false }) {
  const initial = (member.name || '?')[0].toUpperCase();
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.25rem' }}>
      <div style={{ width:size, height:size, borderRadius:'50%', overflow:'hidden', border:`2px solid ${member.confirmed ? G.green : G.border}`, flexShrink:0, background:'var(--dark)', display:'flex', alignItems:'center', justifyContent:'center' }}>
        {member.photo
          ? <img src={member.photo} alt={member.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
          : <span style={{ fontSize: size * 0.35, color:G.gold, fontWeight:600 }}>{initial}</span>}
      </div>
      {showName && <span style={{ fontSize:'0.6rem', color: member.confirmed ? G.white : G.muted, maxWidth:48, textAlign:'center', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{member.name}</span>}
    </div>
  );
}

function StatusPill({ status, tr }) {
  const config = {
    proposed:  { label: tr('scout.status_proposed'),  bg:'rgba(201,168,76,0.12)',  border:G.goldBdr,                  color:G.gold  },
    confirmed: { label: tr('scout.status_confirmed'), bg:'rgba(78,201,148,0.12)',  border:'rgba(78,201,148,0.35)',    color:G.green },
    waiting:   { label: tr('scout.status_waiting'),   bg:'rgba(160,132,201,0.12)', border:'rgba(160,132,201,0.35)',   color:G.purple },
    past:      { label: tr('scout.status_past'),       bg:'rgba(255,255,255,0.05)', border:G.border,                  color:G.muted },
    cancelled: { label: tr('scout.cancelled'),         bg:'rgba(224,112,112,0.1)',  border:'rgba(224,112,112,0.25)',   color:G.red },
  };
  const c = config[status] || config.waiting;
  return (
    <span style={{ fontSize:'0.6rem', fontWeight:600, padding:'0.2rem 0.55rem', borderRadius:99, background:c.bg, border:`1px solid ${c.border}`, color:c.color, letterSpacing:'0.04em' }}>
      {c.label}
    </span>
  );
}

// ── Circle Card ───────────────────────────────────────────────────────────
function CircleCard({ circle, currentUserId, onOpen, tr, lang }) {
  const type = SCOUT_TYPES[circle.type] || SCOUT_TYPES.dinner;
  const members = circle.members || [];
  const sharedInterests = circle.sharedInterests || [];
  const confirmed = members.filter(m => m.confirmed).length;
  const me = members.find(m => m.id === currentUserId);
  const iConfirmed = me?.confirmed;
  const countdown = circle.confirmDeadline ? formatDeadlineCountdown(circle.confirmDeadline) : null;
  const isPast = circle.status === 'past';
  const hasPostVote = isPast && me && !me.voted;

  return (
    <div onClick={() => onOpen(circle)}
      style={{ background:G.card, border:`1px solid ${hasPostVote ? G.goldBdr : circle.status==='proposed' && !iConfirmed ? 'rgba(201,168,76,0.5)' : G.border}`, borderRadius:12, padding:'1rem', cursor:'pointer', transition:'border-color 0.2s', position:'relative', overflow:'hidden' }}>

      {/* Urgent badge */}
      {circle.status === 'proposed' && !iConfirmed && (
        <div style={{ position:'absolute', top:0, right:0, background:G.gold, color:'#0A0A0A', fontSize:'0.58rem', fontWeight:700, padding:'0.2rem 0.6rem', borderRadius:'0 12px 0 8px', letterSpacing:'0.06em' }}>
          ACTION REQUIRED
        </div>
      )}
      {hasPostVote && (
        <div style={{ position:'absolute', top:0, right:0, background:G.purple, color:'white', fontSize:'0.58rem', fontWeight:700, padding:'0.2rem 0.6rem', borderRadius:'0 12px 0 8px', letterSpacing:'0.06em' }}>
          VOTE NOW
        </div>
      )}

      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'0.7rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={{ fontSize:'1.1rem' }}>{type.icon}</span>
          <div>
            <div style={{ fontSize:'0.82rem', fontWeight:600, color:G.white, fontFamily:G.sans }}>{tr(`circles.type_${circle.type}`)}</div>
            {circle.date
              ? <div style={{ fontSize:'0.68rem', color:G.muted }}>{formatScoutDate(circle.date, lang)} · {circle.time}</div>
              : <div style={{ fontSize:'0.68rem', color:G.muted }}>{tr('scout.status_waiting')}</div>}
          </div>
        </div>
        <StatusPill status={circle.status} tr={tr}/>
      </div>

      {/* Venue */}
      {circle.venue && (
        <div style={{ fontSize:'0.7rem', color:G.muted, marginBottom:'0.6rem' }}>
          · {circle.venue.name} · {circle.venue.area}
        </div>
      )}

      {/* Members */}
      {members.length > 0 && (
        <div style={{ display:'flex', gap:'0.4rem', alignItems:'center', marginBottom:'0.6rem', flexWrap:'wrap' }}>
          {members.map(m => <MemberAvatar key={m.id || m.user_id} member={m} size={32}/>)}
          <div style={{ fontSize:'0.65rem', color:G.muted, marginLeft:'0.2rem' }}>
            {tr('scout.members_confirmed', { n: confirmed, total: members.length })}
          </div>
        </div>
      )}

      {/* Shared interests */}
      {sharedInterests.length > 0 && (
        <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap', marginBottom:'0.6rem' }}>
          {sharedInterests.slice(0,4).map(i => <InterestTag key={i} interest={i}/>)}
        </div>
      )}

      {/* Countdown or confirmation progress */}
      {circle.status === 'proposed' && countdown && members.length > 0 && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'0.4rem' }}>
          <span style={{ fontSize:'0.65rem', color:G.muted }}>{tr('scout.deadline')} {countdown}</span>
          <div style={{ height:3, width:100, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${Math.round((confirmed/members.length)*100)}%`, background: confirmed >= circle.minConfirmations ? G.green : G.gold, borderRadius:2, transition:'width 0.4s' }}/>
          </div>
        </div>
      )}

      {/* Waitlist position */}
      {circle.status === 'waiting' && circle.waitlistPosition && (
        <div style={{ fontSize:'0.68rem', color:G.purple }}>
          {tr('scout.waitlist_pos', { n: circle.waitlistPosition })}
        </div>
      )}
    </div>
  );
}

// ── Proposal Detail Sheet ─────────────────────────────────────────────────
function ProposalSheet({ circle, currentUserId, onConfirm, onDecline, onClose, tr, lang }) {
  const type = SCOUT_TYPES[circle.type] || SCOUT_TYPES.dinner;
  const members = circle.members || [];
  const sharedInterests = circle.sharedInterests || [];
  const confirmed = members.filter(m => m.confirmed).length;
  const me = members.find(m => m.id === currentUserId);
  const iConfirmed = me?.confirmed;
  const countdown = circle.confirmDeadline ? formatDeadlineCountdown(circle.confirmDeadline) : null;

  return (
    <div style={{ padding:'1.5rem', maxHeight:'88vh', overflowY:'auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.2rem' }}>
        <div>
          <div style={{ fontSize:'0.6rem', letterSpacing:'0.15em', textTransform:'uppercase', color:G.muted, marginBottom:'0.2rem' }}>{tr('scout.proposal_sub')}</div>
          <div style={{ fontFamily:G.serif, fontSize:'1.5rem', color:G.white }}>{type.icon} {tr(`circles.type_${circle.type}`)}</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.muted, cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
      </div>

      {/* Date + venue */}
      <div style={{ background:'rgba(201,168,76,0.06)', border:`1px solid ${G.goldBdr}`, borderRadius:10, padding:'0.9rem 1rem', marginBottom:'1.2rem' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.8rem' }}>
          <div>
            <div style={{ fontSize:'0.58rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.2rem' }}>Date</div>
            <div style={{ fontSize:'0.82rem', color:G.white, fontWeight:600 }}>{formatScoutDate(circle.date, lang)}</div>
            <div style={{ fontSize:'0.72rem', color:G.gold }}>{circle.time}</div>
          </div>
          <div>
            <div style={{ fontSize:'0.58rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.2rem' }}>Lieu</div>
            <div style={{ fontSize:'0.82rem', color:G.white, fontWeight:600 }}>{circle.venue?.name}</div>
            <div style={{ fontSize:'0.72rem', color:G.muted }}>{circle.venue?.area}</div>
          </div>
        </div>
      </div>

      {/* Your group */}
      <div style={{ marginBottom:'1.2rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem' }}>Ton groupe</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(64px, 1fr))', gap:'0.5rem' }}>
          {members.map(m => (
            <div key={m.id || m.user_id} style={{ textAlign:'center' }}>
              <MemberAvatar member={m} size={44} showName={true}/>
              {m.confirmed && <div style={{ fontSize:'0.55rem', color:G.green, marginTop:'0.1rem' }}>✓</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Shared interests */}
      {sharedInterests.length > 0 && (
        <div style={{ marginBottom:'1.2rem' }}>
          <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>
            {tr('scout.interests_match', { n: sharedInterests.length })}
          </div>
          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
            {sharedInterests.map(i => <InterestTag key={i} interest={i}/>)}
          </div>
        </div>
      )}

      {/* Confirmation progress */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:8, padding:'0.7rem 0.9rem', marginBottom:'1.2rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.4rem' }}>
          <span style={{ fontSize:'0.7rem', color:G.muted }}>{tr('scout.members_confirmed', { n: confirmed, total: members.length })}</span>
          <span style={{ fontSize:'0.7rem', color: confirmed >= circle.minConfirmations ? G.green : G.gold }}>{confirmed >= circle.minConfirmations ? '✓ Minimum atteint' : `${circle.minConfirmations - confirmed} de plus requis`}</span>
        </div>
        <div style={{ height:4, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${members.length ? Math.round((confirmed/members.length)*100) : 0}%`, background: confirmed >= circle.minConfirmations ? G.green : G.gold, borderRadius:2, transition:'width 0.4s' }}/>
        </div>
        <div style={{ fontSize:'0.62rem', color:G.muted, marginTop:'0.35rem' }}>
          {tr('scout.min_warning')} · {tr('scout.expires_in', { h: countdown })}
        </div>
      </div>

      {/* CTA */}
      {!iConfirmed ? (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          <button onClick={onConfirm}
            style={{ width:'100%', padding:'0.85rem', background:G.gold, border:'none', borderRadius:8, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.85rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.05em' }}>
            {tr('scout.confirm_cta')}
          </button>
          <button onClick={onDecline}
            style={{ width:'100%', padding:'0.65rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:8, color:G.muted, fontFamily:G.sans, fontSize:'0.75rem', cursor:'pointer' }}>
            {tr('scout.decline_cta')}
          </button>
        </div>
      ) : (
        <div style={{ textAlign:'center', padding:'0.8rem', background:'rgba(78,201,148,0.08)', border:'1px solid rgba(78,201,148,0.25)', borderRadius:8 }}>
          <div style={{ fontSize:'0.9rem', color:G.green, fontWeight:600 }}>{tr('scout.confirmed')}</div>
          <div style={{ fontSize:'0.68rem', color:G.muted, marginTop:'0.2rem' }}>On t'enverra l'adresse complète 24h avant</div>
        </div>
      )}
    </div>
  );
}

// ── Post-Circle Vote Sheet ────────────────────────────────────────────────
function PostCircleSheet({ circle, currentUserId, onVote, onClose, tr }) {
  const [votes, setVotes] = useState({});
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const others = (circle.members || []).filter(m => m.id !== currentUserId);

  const handleSubmitVotes = () => {
    setRevealing(true);
    setTimeout(() => {
      setRevealed(true);
      setRevealing(false);
      onVote?.(votes);
    }, 1200);
  };

  const mutualMatches = circle.postScout?.mutualMatches || [];
  const scores = circle.postScout?.revealedScores || {};

  if (revealed) {
    return (
      <div style={{ padding:'1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'1.2rem' }}>
          <div style={{ fontFamily:G.serif, fontSize:'1.3rem', color:G.white }}>{tr('scout.reveal_scores')}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:G.muted, cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
          {others.map(m => {
            const isMutual = mutualMatches.includes(m.id);
            const score = scores[m.id];
            return (
              <div key={m.id} style={{ background:G.card, border:`1px solid ${isMutual ? 'rgba(78,201,148,0.4)' : G.border}`, borderRadius:10, padding:'0.9rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.8rem', marginBottom:'0.5rem' }}>
                  <MemberAvatar member={m} size={40}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'0.85rem', fontWeight:600, color:G.white }}>{m.name}</div>
                    {isMutual && <div style={{ fontSize:'0.65rem', color:G.green, fontWeight:700 }}>✓ {tr('scout.post_match')}</div>}
                  </div>
                  {score && (
                    <div style={{ fontFamily:G.serif, fontSize:'1.6rem', color: isMutual ? G.green : G.gold, lineHeight:1 }}>
                      {score.score.toFixed(1)}
                    </div>
                  )}
                </div>
                {score?.axes && (
                  <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap' }}>
                    {Object.entries(score.axes).map(([axis, val]) => (
                      <div key={axis} style={{ fontSize:'0.6rem', padding:'0.15rem 0.4rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:4, color:G.muted }}>
                        {axis}: <span style={{ color:G.white }}>{val.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={onClose}
          style={{ marginTop:'1rem', width:'100%', padding:'0.65rem', background:G.gold, border:'none', borderRadius:8, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
          Fermer
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.5rem' }}>
        <div style={{ fontFamily:G.serif, fontSize:'1.3rem', color:G.white }}>{tr('scout.post_vote')}</div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.muted, cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
      </div>
      <p style={{ fontSize:'0.72rem', color:G.muted, marginBottom:'1.2rem', lineHeight:1.6 }}>
        Ton vote est anonyme. Si deux personnes se likent mutuellement, un match est créé et les scores de compatibilité sont révélés.
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.7rem', marginBottom:'1.2rem' }}>
        {others.map(m => (
          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:'0.9rem', background:G.card, border:`1px solid ${votes[m.id] ? G.goldBdr : G.border}`, borderRadius:10, padding:'0.8rem' }}>
            <MemberAvatar member={m} size={40}/>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:'0.85rem', fontWeight:600, color:G.white }}>{m.name}, {m.age}</div>
            </div>
            <div style={{ display:'flex', gap:'0.4rem' }}>
              {['yes','no'].map(v => (
                <button key={v} onClick={() => setVotes(prev => ({...prev, [m.id]: v}))}
                  style={{ width:36, height:36, borderRadius:8, border:`1px solid ${votes[m.id]===v ? (v==='yes' ? 'rgba(78,201,148,0.5)' : 'rgba(224,112,112,0.5)') : G.border}`, background: votes[m.id]===v ? (v==='yes' ? 'rgba(78,201,148,0.15)' : 'rgba(224,112,112,0.1)') : 'transparent', color: v==='yes' ? G.green : G.red, cursor:'pointer', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {v === 'yes' ? '♥' : '✕'}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={handleSubmitVotes}
        disabled={Object.keys(votes).length < others.length || revealing}
        style={{ width:'100%', padding:'0.85rem', background: Object.keys(votes).length >= others.length ? G.gold : 'rgba(255,255,255,0.1)', border:'none', borderRadius:8, color: Object.keys(votes).length >= others.length ? '#0A0A0A' : G.muted, fontFamily:G.sans, fontSize:'0.82rem', fontWeight:700, cursor: Object.keys(votes).length >= others.length ? 'pointer' : 'default' }}>
        {revealing ? 'Calcul des matchs...' : tr('scout.reveal_scores')} →
      </button>
    </div>
  );
}

// ── Join Waitlist Sheet ───────────────────────────────────────────────────
const SCOUT_DAYS = [
  { id:'mon', labelKey:'scout.day_mon' },
  { id:'tue', labelKey:'scout.day_tue' },
  { id:'wed', labelKey:'scout.day_wed' },
  { id:'thu', labelKey:'scout.day_thu' },
  { id:'fri', labelKey:'scout.day_fri' },
  { id:'sat', labelKey:'scout.day_sat' },
  { id:'sun', labelKey:'scout.day_sun' },
];
const SCOUT_TIME_SLOTS = [
  { id:'lunch',      icon:'☀️', labelKey:'scout.slot_lunch' },
  { id:'after_work', icon:'✦', labelKey:'scout.slot_after_work' },
  { id:'dinner',     icon:'✦', labelKey:'scout.slot_dinner' },
  { id:'brunch',     icon:'✦', labelKey:'scout.slot_brunch' },
];
const SCOUT_LANG_PREFS = [
  { id:'fr',   labelKey:'scout.lang_fr' },
  { id:'en',   labelKey:'scout.lang_en' },
  { id:'both', labelKey:'scout.lang_both' },
];
const SCOUT_SIZE_PREFS = [
  { id:'small', labelKey:'scout.size_small' },
  { id:'large', labelKey:'scout.size_large' },
  { id:'any',   labelKey:'scout.size_any' },
];
// All 8 types come from SCOUT_TYPES in scoutAlgo
const ALL_SCOUT_TYPES = SCOUT_TYPES;

function JoinWaitlistSheet({ onJoin, onClose, tr }) {
  const savedPrefs = loadScoutPrefs();
  const [type,      setType]      = useState(savedPrefs?.type || 'dinner');
  const [days,      setDays]      = useState(savedPrefs?.days_available || []);
  const [slots,     setSlots]     = useState(savedPrefs?.time_slots || []);
  const [langPref,  setLangPref]  = useState(savedPrefs?.lang_pref || 'both');
  const [groupSize, setGroupSize] = useState(savedPrefs?.group_size_pref || 'any');

  const toggleArr = (arr, setArr, val) =>
    setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const handleJoin = () => {
    const prefs = { type, days_available: days, time_slots: slots, lang_pref: langPref, group_size_pref: groupSize };
    saveScoutPrefs(prefs);
    onJoin(prefs);
  };

  const pill = (active) => ({
    padding:'0.38rem 0.6rem', background: active ? G.goldPale : 'transparent',
    border:`1px solid ${active ? G.gold : G.border}`, borderRadius:20,
    color: active ? G.gold : G.muted, fontFamily:G.sans, fontSize:'0.7rem',
    cursor:'pointer', transition:'all 0.12s',
  });

  return (
    <div style={{ padding:'1.5rem', overflowY:'auto', maxHeight:'85vh' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'1.2rem' }}>
        <div style={{ fontFamily:G.serif, fontSize:'1.4rem', color:G.white }}>{tr('scout.join_cta')}</div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.muted, cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
      </div>

      {/* Activity type */}
      <div style={{ marginBottom:'1.1rem' }}>
        <div style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{tr('scout.type_label')}</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.45rem' }}>
          {Object.entries(ALL_SCOUT_TYPES).map(([id, ct]) => (
            <button key={id} onClick={() => setType(id)}
              style={{ padding:'0.55rem 0.7rem', background: type===id ? G.goldPale : 'transparent', border:`1px solid ${type===id ? G.gold : G.border}`, borderRadius:8, color: type===id ? G.gold : G.muted, fontFamily:G.sans, fontSize:'0.72rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.4rem', textAlign:'left' }}>
              <span style={{ fontSize:'0.95rem' }}>{ct.icon}</span>
              <span>{tr(`circles.type_${id}`)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Days available */}
      <div style={{ marginBottom:'1.1rem' }}>
        <div style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{tr('scout.days_label')}</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.35rem' }}>
          {SCOUT_DAYS.map(d => (
            <button key={d.id} onClick={() => toggleArr(days, setDays, d.id)}
              style={pill(days.includes(d.id))}>
              {tr(d.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Time of day */}
      <div style={{ marginBottom:'1.1rem' }}>
        <div style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{tr('scout.time_label')}</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.35rem' }}>
          {SCOUT_TIME_SLOTS.map(s => (
            <button key={s.id} onClick={() => toggleArr(slots, setSlots, s.id)}
              style={pill(slots.includes(s.id))}>
              {s.icon} {tr(s.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Language preference */}
      <div style={{ marginBottom:'1.1rem' }}>
        <div style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{tr('scout.lang_label')}</div>
        <div style={{ display:'flex', gap:'0.4rem' }}>
          {SCOUT_LANG_PREFS.map(p => (
            <button key={p.id} onClick={() => setLangPref(p.id)}
              style={{ flex:1, padding:'0.42rem 0', background: langPref===p.id ? G.goldPale : 'transparent', border:`1px solid ${langPref===p.id ? G.gold : G.border}`, borderRadius:8, color: langPref===p.id ? G.gold : G.muted, fontFamily:G.sans, fontSize:'0.7rem', cursor:'pointer' }}>
              {tr(p.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Group size */}
      <div style={{ marginBottom:'1.4rem' }}>
        <div style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{tr('scout.size_label')}</div>
        <div style={{ display:'flex', gap:'0.4rem' }}>
          {SCOUT_SIZE_PREFS.map(p => (
            <button key={p.id} onClick={() => setGroupSize(p.id)}
              style={{ flex:1, padding:'0.42rem 0.3rem', background: groupSize===p.id ? G.goldPale : 'transparent', border:`1px solid ${groupSize===p.id ? G.gold : G.border}`, borderRadius:8, color: groupSize===p.id ? G.gold : G.muted, fontFamily:G.sans, fontSize:'0.67rem', cursor:'pointer', lineHeight:1.3 }}>
              {tr(p.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:'0.7rem 0.9rem', background:'rgba(91,155,213,0.06)', border:'1px solid rgba(91,155,213,0.15)', borderRadius:8, marginBottom:'1.2rem' }}>
        <p style={{ fontSize:'0.68rem', color:G.muted, margin:0, lineHeight:1.6 }}>
          {tr('scout.waitlist_info')}
        </p>
      </div>

      <button onClick={handleJoin}
        style={{ width:'100%', padding:'0.85rem', background:G.gold, border:'none', borderRadius:8, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.85rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.05em' }}>
        {tr('scout.join_cta')} ✦
      </button>
    </div>
  );
}

// ── How it works section ──────────────────────────────────────────────────
function HowItWorks({ tr }) {
  const steps = [1,2,3,4,5,6].map(n => tr(`circles.how_${n}`));
  return (
    <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1.2rem', marginBottom:'1.2rem' }}>
      <div style={{ fontSize:'0.65rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.9rem' }}>{tr('scout.how_title')}</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display:'flex', gap:'0.7rem', alignItems:'flex-start' }}>
            <div style={{ width:20, height:20, borderRadius:'50%', background: i < 3 ? G.goldPale : 'rgba(255,255,255,0.05)', border:`1px solid ${i < 3 ? G.gold : G.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
              <span style={{ fontSize:'0.6rem', color: i < 3 ? G.gold : G.muted, fontWeight:700 }}>{i+1}</span>
            </div>
            <span style={{ fontSize:'0.75rem', color: i === 5 ? G.purple : G.muted, lineHeight:1.5 }}>{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Invitation Card (pending invite from another user) ────────────────────
function InvitationCard({ invite, lang, onAccept, onDecline }) {
  const fr = lang === 'fr';
  const circle  = invite.circle || {};
  const host    = invite.host || {};
  const typeCfg = SCOUT_TYPES[circle.type] || SCOUT_TYPES.dinner;
  const dates   = circle.proposed_dates || [];
  const [responding, setResponding] = useState(false);
  const [declined,   setDeclined]   = useState(false);

  const handleDecline = async () => {
    setResponding(true);
    await onDecline(invite.id);
    setDeclined(true);
  };

  if (declined) return null;

  const isDate = circle.type === 'date';
  const TYPE_LABELS_FR = { dinner:'Dîner', apero:'Apéro', coffee:'Café', outdoor:'Sortie nature', culture:'Culture', board_games:'Jeux', sport:'Sport', workshop:'Atelier', date:'Rendez-vous', mixer:'Mixer' };
  const TYPE_LABELS_EN = { dinner:'Dinner', apero:'Apéro', coffee:'Coffee', outdoor:'Outdoor', culture:'Culture', board_games:'Board games', sport:'Sport', workshop:'Workshop', date:'Date', mixer:'Mixer' };

  return (
    <div style={{ background:G.card, border:`1px solid ${isDate ? 'rgba(201,168,76,0.6)' : G.goldBdr}`, borderRadius:12, padding:'1rem', marginBottom:'0.8rem', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, right:0, background: isDate ? 'linear-gradient(135deg, #C9A84C, #E8C87A)' : G.gold, color:'#0A0A0A', fontSize:'0.56rem', fontWeight:700, padding:'0.18rem 0.55rem', borderRadius:'0 12px 0 8px', letterSpacing:'0.06em' }}>
        {isDate ? (fr ? 'RENDEZ-VOUS' : 'DATE') : (fr ? 'INVITATION' : 'INVITE')}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.55rem', marginBottom:'0.75rem' }}>
        <span style={{ fontSize:'1.2rem' }}>{typeCfg.icon}</span>
        <div>
          <div style={{ fontSize:'0.85rem', fontWeight:600, color:G.white }}>
            {fr ? TYPE_LABELS_FR[circle.type] : TYPE_LABELS_EN[circle.type]}
          </div>
          <div style={{ fontSize:'0.68rem', color:G.muted }}>
            {isDate
              ? (fr ? `${host.name} t'invite à un rendez-vous` : `${host.name} invites you on a date`)
              : (fr ? `Invité·e par ${host.name}` : `Invited by ${host.name}`)
            }
            {circle.location_area ? ` · ${circle.location_area}` : ''}
          </div>
        </div>
      </div>
      {dates.length > 0 && (
        <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', marginBottom:'0.75rem' }}>
          {dates.map((d, i) => (
            <span key={i} style={{ fontSize:'0.65rem', padding:'0.2rem 0.55rem', background:'rgba(201,168,76,0.08)', border:`1px solid ${G.goldBdr}`, borderRadius:99, color:G.gold }}>
              {d.date ? new Date(d.date + 'T12:00').toLocaleDateString(fr ? 'fr-FR' : 'en-US', { weekday:'short', month:'short', day:'numeric' }) : ''}
              {d.time ? ` · ${fr
                ? { morning:'matin', afternoon:'après-midi', evening:'soir', night:'nuit' }[d.time]
                : d.time}` : ''}
            </span>
          ))}
        </div>
      )}
      <div style={{ display:'flex', gap:'0.5rem' }}>
        <button onClick={() => onAccept(invite)} disabled={responding}
          className="btn btn-gold btn-sm" style={{ flex:2 }}>
          {fr ? 'Accepter' : 'Accept'}
        </button>
        <button onClick={handleDecline} disabled={responding}
          className="btn btn-outline btn-sm" style={{ flex:1 }}>
          {fr ? 'Décliner' : 'Decline'}
        </button>
      </div>
    </div>
  );
}

// ── RSVP sheet — invitee picks their preferred dates ─────────────────────
function RsvpSheet({ invite, lang, onSubmit, onClose }) {
  const fr = lang === 'fr';
  const circle  = invite.circle || {};
  const dates   = circle.proposed_dates || [];
  const [picked, setPicked]   = useState(new Set());
  const [sending, setSending] = useState(false);

  const toggle = (i) => setPicked(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  const submit = async () => {
    setSending(true);
    const preferred = dates.filter((_, i) => picked.has(i));
    await onSubmit(invite.id, preferred);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxHeight:'72vh' }}>
        <div style={{ textAlign:'center', padding:'0.6rem 0 0' }}>
          <div style={{ width:32, height:2, background:'var(--muted2)', margin:'0 auto', borderRadius:99 }}/>
        </div>
        <div style={{ padding:'1.2rem 1.2rem 2rem', overflowY:'auto' }}>
          <div style={{ fontFamily:G.serif, fontSize:'1.3rem', color:G.white, marginBottom:'0.2rem' }}>
            {fr ? 'Tes disponibilités' : 'Your availability'}
          </div>
          <p style={{ fontSize:'0.72rem', color:G.muted, marginBottom:'1.1rem', lineHeight:1.6 }}>
            {fr
              ? 'Coche les créneaux qui te conviennent (optionnel).'
              : 'Tick the slots that work for you (optional).'}
          </p>
          {dates.length === 0 ? (
            <p style={{ color:G.muted, fontSize:'0.78rem', textAlign:'center' }}>
              {fr ? 'Aucun créneau proposé.' : 'No slots proposed yet.'}
            </p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.45rem', marginBottom:'1.2rem' }}>
              {dates.map((d, i) => (
                <button key={i} onClick={() => toggle(i)}
                  style={{
                    padding:'0.7rem 0.9rem', textAlign:'left',
                    background: picked.has(i) ? G.goldPale : G.card,
                    border:`1px solid ${picked.has(i) ? G.gold : G.border}`,
                    borderRadius:10, cursor:'pointer', display:'flex', alignItems:'center', gap:'0.6rem',
                  }}>
                  {picked.has(i) && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={G.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                  <span style={{ fontSize:'0.82rem', color: picked.has(i) ? G.gold : G.white }}>
                    {d.date ? new Date(d.date + 'T12:00').toLocaleDateString(fr ? 'fr-FR' : 'en-US', { weekday:'long', month:'long', day:'numeric' }) : '?'}
                    {d.time ? ` — ${fr
                      ? { morning:'Matin', afternoon:'Après-midi', evening:'Soirée', night:'Nuit' }[d.time]
                      : { morning:'Morning', afternoon:'Afternoon', evening:'Evening', night:'Night' }[d.time]}` : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button onClick={submit} disabled={sending} className="btn btn-gold btn-md btn-full">
            {sending
              ? (fr ? 'Envoi…' : 'Sending…')
              : (fr ? 'Confirmer ma participation' : 'Confirm attendance')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Host Circle Card (circles you created as host) ────────────────────────
const CARD_TYPE_LABELS_FR = { dinner:'Dîner', apero:'Apéro', coffee:'Café', outdoor:'Sortie nature', culture:'Culture', board_games:'Jeux', sport:'Sport', workshop:'Atelier', date:'Rendez-vous', mixer:'Mixer' };
const CARD_TYPE_LABELS_EN = { dinner:'Dinner', apero:'Apéro', coffee:'Coffee', outdoor:'Outdoor', culture:'Culture', board_games:'Board games', sport:'Sport', workshop:'Workshop', date:'Date', mixer:'Mixer' };

function HostCircleCard({ circle, lang, onSendToConcierge }) {
  const fr = lang === 'fr';
  const typeCfg     = SCOUT_TYPES[circle.type] || SCOUT_TYPES.dinner;
  const invitations = circle.invitations || [];
  const accepted    = invitations.filter(i => i.status === 'accepted');
  const pending     = invitations.filter(i => i.status === 'pending');
  const isSent      = circle.status === 'proposed';
  const isDate      = circle.type === 'date';
  const minAccepted = isDate ? 1 : 2;

  // Find most popular date across accepted invitees
  const dateCounts = {};
  accepted.forEach(inv => {
    (inv.preferred_dates || []).forEach(d => {
      const key = `${d.date}|${d.time}`;
      dateCounts[key] = (dateCounts[key] || 0) + 1;
    });
  });
  const topDate = Object.entries(dateCounts).sort((a,b) => b[1]-a[1])[0];

  return (
    <div style={{ background:G.card, border:`1px solid ${isSent ? 'rgba(78,201,148,0.4)' : G.goldBdr}`, borderRadius:12, padding:'1rem', marginBottom:'0.8rem' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'0.6rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span style={{ fontSize:'1.1rem' }}>{typeCfg.icon}</span>
          <div>
            <div style={{ fontSize:'0.82rem', fontWeight:600, color:G.white }}>
              {fr ? CARD_TYPE_LABELS_FR[circle.type] : CARD_TYPE_LABELS_EN[circle.type]}
              {circle.location_area && <span style={{ color:G.muted, fontWeight:400 }}> · {circle.location_area}</span>}
            </div>
            <div style={{ fontSize:'0.65rem', color:G.muted }}>
              {isDate ? (fr ? 'Ton rendez-vous privé' : 'Your private date') : (fr ? 'Ton cercle privé' : 'Your private circle')}
            </div>
          </div>
        </div>
        <span style={{
          fontSize:'0.58rem', fontWeight:700, padding:'0.18rem 0.5rem', borderRadius:99, letterSpacing:'0.04em',
          background: isSent ? 'rgba(78,201,148,0.12)' : G.goldPale,
          border:`1px solid ${isSent ? 'rgba(78,201,148,0.35)' : G.goldBdr}`,
          color: isSent ? '#4EC994' : G.gold,
        }}>
          {isSent ? (fr ? 'Envoyé au concierge' : 'Sent to concierge') : (fr ? 'En attente' : 'Pending')}
        </span>
      </div>

      {/* Response summary */}
      <div style={{ display:'flex', gap:'0.8rem', marginBottom:'0.7rem' }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:'1.1rem', fontWeight:700, color:'#4EC994' }}>{accepted.length}</div>
          <div style={{ fontSize:'0.6rem', color:G.muted }}>{fr ? 'Accepté' : 'Accepted'}</div>
        </div>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:'1.1rem', fontWeight:700, color:G.gold }}>{pending.length}</div>
          <div style={{ fontSize:'0.6rem', color:G.muted }}>{fr ? 'En attente' : 'Pending'}</div>
        </div>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:'1.1rem', fontWeight:700, color:'var(--muted2)' }}>
            {invitations.filter(i => i.status === 'declined').length}
          </div>
          <div style={{ fontSize:'0.6rem', color:G.muted }}>{fr ? 'Décliné' : 'Declined'}</div>
        </div>
      </div>

      {/* Most popular date */}
      {topDate && (
        <div style={{ fontSize:'0.68rem', color:G.gold, marginBottom:'0.6rem', padding:'0.3rem 0.6rem', background:G.goldPale, borderRadius:99, display:'inline-block', border:`1px solid ${G.goldBdr}` }}>
          {fr ? '🗓 Créneau populaire : ' : '🗓 Top slot: '}
          {(() => {
            const [dateStr, time] = topDate[0].split('|');
            const label = new Date(dateStr + 'T12:00').toLocaleDateString(fr ? 'fr-FR' : 'en-US', { weekday:'short', month:'short', day:'numeric' });
            const tl = fr
              ? { morning:'matin', afternoon:'après-midi', evening:'soir', night:'nuit' }[time]
              : time;
            return `${label} ${tl}`;
          })()}
        </div>
      )}

      {/* Accepted member avatars */}
      {accepted.length > 0 && (
        <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap', marginBottom:'0.7rem' }}>
          {accepted.map(inv => (
            <div key={inv.id} title={inv.invitee?.name}
              style={{ width:30, height:30, borderRadius:'50%', background:'var(--mid)', border:`2px solid #4EC994`, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', color:G.muted }}>
              {inv.invitee?.photos?.[0]
                ? <img src={inv.invitee.photos[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : (inv.invitee?.name?.[0] || '?')}
            </div>
          ))}
        </div>
      )}

      {!isSent && accepted.length >= minAccepted && (
        <button onClick={() => onSendToConcierge(circle.id)} className="btn btn-gold btn-sm btn-full">
          {isDate
            ? (fr ? '💫 Réserver pour votre rendez-vous' : '💫 Book your date')
            : (fr ? '✦ Envoyer au concierge' : '✦ Send to concierge')}
        </button>
      )}
      {!isSent && accepted.length < minAccepted && (
        <p style={{ fontSize:'0.67rem', color:G.muted, textAlign:'center', margin:0 }}>
          {isDate
            ? (fr ? 'En attente de réponse…' : 'Waiting for response…')
            : (fr
              ? `En attente de réponses (${minAccepted - accepted.length} acceptation${minAccepted - accepted.length > 1 ? 's' : ''} nécessaire${minAccepted - accepted.length > 1 ? 's' : ''})`
              : `Waiting for responses (need ${minAccepted - accepted.length} more acceptance${minAccepted - accepted.length > 1 ? 's' : ''})`)}
        </p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function Circles() {
  const tr = useTr();
  const { features, currentUser, lang, activeNetwork, joinScoutWaitlist, confirmScout, voteScout } = useApp();

  if (features?.circles === false) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem', textAlign:'center' }}>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'3rem', color:'var(--gold)', marginBottom:'1rem', lineHeight:1 }}>✦</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'var(--white)', marginBottom:'0.5rem' }}>
            {tr('scout.coming_soon')}
          </h2>
          <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.7 }}>
            {tr('common.stay_tuned')}
          </p>
        </div>
      </div>
    );
  }
  const userId = currentUser?.id || 'me';

  const [scouts,         setScouts]         = useState([]);
  const [myInvitations,  setMyInvitations]  = useState([]);
  const [myHostedScouts, setMyHostedScouts] = useState([]);
  const [rsvpInvite,     setRsvpInvite]     = useState(null); // invite being RSVP'd
  const [openCircle,     setOpenCircle]     = useState(null);
  const [showSheet,      setShowSheet]      = useState(null); // 'proposal'|'vote'|'join'|'create'
  const [onWaitlist,     setOnWaitlist]     = useState(!!loadScoutPrefs());

  // Load member circles, invitations, and hosted circles from Supabase
  useEffect(() => {
    if (!currentUser?.id) return;
    import('../utils/supabase').then(({ supabase, db }) => {
      if (!supabase) return;

      // Load invitations I received
      supabase
        .from('scout_invitations')
        .select(`
          id, status, preferred_dates, created_at,
          scout:scouts(id, type, activity_type, proposed_dates, venue_type, location_area, status),
          host:users!invited_by(id, name, photos)
        `)
        .eq('invitee_id', currentUser.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setMyInvitations(data); });

      // Load scouts I created as host
      supabase
        .from('scouts')
        .select(`
          id, type, activity_type, proposed_dates, venue_type, location_area, status, created_at,
          invitations:scout_invitations(
            id, status, preferred_dates,
            invitee:users!invitee_id(id, name, photos)
          )
        `)
        .eq('host_id', currentUser.id)
        .not('status', 'eq', 'past')
        .order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setMyHostedScouts(data); });

      // Load member scouts (existing logic)
      db.scouts()
        ?.select(`
          *,
          scout_members!inner(user_id, confirmed, voted, vote),
          members:scout_members(
            user_id, confirmed, voted, vote,
            user:user_core!user_id(id, name, age, gender)
          )
        `)
        .order('created_at', { ascending: false })
        .then(({ data, error }) => {
          if (data && !error) {
            const normalized = data.map(c => ({
              ...c,
              members: (c.members || []).map(m => ({
                id:        m.user?.id || m.user_id,
                name:      m.user?.name || '',
                age:       m.user?.age,
                gender:    m.user?.gender,
                confirmed: m.confirmed,
                voted:     m.voted,
                vote:      m.vote,
              })),
              sharedInterests: c.sharedInterests || [],
            }));
            setScouts(normalized);
          }
        });
    });
  }, [currentUser?.id]);


  const proposed = scouts.filter(c => c.status === 'proposed');
  const past     = scouts.filter(c => c.status === 'past');
  const waiting  = scouts.filter(c => c.status === 'waiting');

  const openSheet = (circle, type) => { setOpenCircle(circle); setShowSheet(type); };

  const handleConfirm = async () => {
    if (openCircle && confirmScout) await confirmScout(openCircle.id, true);
    setScouts(prev => prev.map(c => c.id !== openCircle?.id ? c : {
      ...c, members: (c.members || []).map(m => m.id === userId ? {...m, confirmed: true} : m),
    }));
    setShowSheet(null);
  };

  const handleDecline = async () => {
    if (openCircle && confirmScout) await confirmScout(openCircle.id, false);
    setScouts(prev => prev.filter(c => c.id !== openCircle?.id));
    setOpenCircle(null); setShowSheet(null);
  };

  const handleVote = async (votes) => {
    if (openCircle && voteScout) {
      const myVote = votes[userId];
      await voteScout(openCircle.id, myVote ?? true);
    }
    setScouts(prev => prev.map(c => c.id !== openCircle?.id ? c : {
      ...c, members: (c.members || []).map(m => m.id === userId ? {...m, voted: true, vote: votes[m.id]} : m),
    }));
  };

  const handleJoinWaitlist = async (prefs) => {
    await joinScoutWaitlist({
      networkId:       currentUser?.network_id || activeNetwork,
      scoutType:       prefs.type,
      days_available:  prefs.days_available || [],
      time_slots:      prefs.time_slots || [],
      lang_pref:       prefs.lang_pref || 'both',
      group_size_pref: prefs.group_size_pref || 'any',
    });
    setOnWaitlist(true);
    setShowSheet(null);
  };

  // Invitation RSVP — user accepts → show date picker sheet
  const handleAcceptInvite = (invite) => setRsvpInvite(invite);

  const handleRsvpSubmit = async (inviteId, preferredDates) => {
    const { supabase } = await import('../utils/supabase');
    await supabase.from('scout_invitations').update({
      status:          'accepted',
      preferred_dates: preferredDates,
      responded_at:    new Date().toISOString(),
    }).eq('id', inviteId);
    setMyInvitations(prev => prev.filter(i => i.id !== inviteId));
    setRsvpInvite(null);
    // Refresh hosted circles of the host (they'll see it in their next load)
  };

  const handleDeclineInvite = async (inviteId) => {
    const { supabase } = await import('../utils/supabase');
    await supabase.from('scout_invitations').update({
      status:       'declined',
      responded_at: new Date().toISOString(),
    }).eq('id', inviteId);
    setMyInvitations(prev => prev.filter(i => i.id !== inviteId));
  };

  const handleSendToConcierge = async (scoutId) => {
    const { supabase } = await import('../utils/supabase');
    await supabase.from('scouts').update({ status: 'proposed' }).eq('id', scoutId);
    setMyHostedScouts(prev => prev.map(c => c.id === scoutId ? { ...c, status: 'proposed' } : c));
  };

  const fr = lang === 'fr';
  const hasAnyContent = proposed.length > 0 || past.length > 0 || waiting.length > 0
    || myInvitations.length > 0 || myHostedScouts.length > 0;

  return (
    <div style={{ minHeight:'100vh', background:G.bg, color:G.white, fontFamily:G.sans, paddingBottom:'calc(var(--bottom-h,56px) + 1rem)' }}>

      {/* Header */}
      <div style={{ padding:'1.2rem 1.2rem 0.5rem', borderBottom:`1px solid ${G.border}`, background:G.bg, position:'sticky', top:0, zIndex:10 }}>
        <div style={{ fontFamily:G.serif, fontSize:'1.6rem', fontWeight:400, color:G.white, marginBottom:'0.1rem' }}>
          <span style={{ color:G.gold, fontStyle:'italic' }}>Bee</span>Love Circles
        </div>
        <div style={{ fontSize:'0.7rem', color:G.muted }}>{tr('scout.subtitle')}</div>
      </div>

      <div style={{ padding:'1rem 1.2rem' }}>

        {/* ── Invitations received ── */}
        {myInvitations.length > 0 && (
          <>
            <div style={{ fontSize:'0.62rem', color:G.gold, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <span style={{ display:'inline-block', width:6, height:6, borderRadius:'50%', background:G.gold }}/>
              {fr ? 'Invitations' : 'Invitations'}
            </div>
            {myInvitations.map(inv => (
              <InvitationCard key={inv.id} invite={inv} lang={lang}
                onAccept={handleAcceptInvite}
                onDecline={handleDeclineInvite}/>
            ))}
          </>
        )}

        {/* ── My hosted circles ── */}
        {myHostedScouts.length > 0 && (
          <>
            <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem', marginTop: myInvitations.length > 0 ? '1rem' : 0 }}>
              {fr ? 'Mes cercles privés' : 'My private circles'}
            </div>
            {myHostedScouts.map(c => (
              <HostCircleCard key={c.id} circle={c} lang={lang} onSendToConcierge={handleSendToConcierge}/>
            ))}
          </>
        )}

        {/* ── Pending proposals (system circles) ── */}
        {proposed.length > 0 && (
          <>
            <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem', marginTop: (myInvitations.length > 0 || myHostedScouts.length > 0) ? '1rem' : 0 }}>
              {fr ? 'Proposition en attente' : 'Pending proposal'}
            </div>
            {proposed.map(c => (
              <div key={c.id} style={{ marginBottom:'0.8rem' }}>
                <CircleCard circle={c} currentUserId={userId} tr={tr} lang={lang}
                  onOpen={circle => openSheet(circle, 'proposal')}/>
              </div>
            ))}
          </>
        )}

        {/* ── Post-vote needed ── */}
        {past.filter(c => (c.members || []).find(m => m.id === userId && !m.voted)).length > 0 && (
          <>
            <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem', marginTop: proposed.length > 0 ? '1rem' : 0 }}>
              {fr ? 'À voter' : 'Vote now'}
            </div>
            {past.filter(c => (c.members || []).find(m => m.id === userId && !m.voted)).map(c => (
              <div key={c.id} style={{ marginBottom:'0.8rem' }}>
                <CircleCard circle={c} currentUserId={userId} tr={tr} lang={lang}
                  onOpen={circle => openSheet(circle, 'vote')}/>
              </div>
            ))}
          </>
        )}

        {/* ── Waitlist ── */}
        {waiting.length > 0 && (
          <>
            <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem', marginTop:'1rem' }}>
              {fr ? 'Liste d\'attente' : 'Waitlist'}
            </div>
            {waiting.map(c => (
              <div key={c.id} style={{ marginBottom:'0.8rem' }}>
                <CircleCard circle={c} currentUserId={userId} tr={tr} lang={lang} onOpen={() => {}}/>
              </div>
            ))}
          </>
        )}

        {/* ── Past circles ── */}
        {past.filter(c => (c.members || []).find(m => m.id === userId && m.voted)).length > 0 && (
          <>
            <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.7rem', marginTop:'1rem' }}>
              {fr ? 'Passés' : 'Past'}
            </div>
            {past.filter(c => (c.members || []).find(m => m.id === userId && m.voted)).map(c => (
              <div key={c.id} style={{ marginBottom:'0.8rem' }}>
                <CircleCard circle={c} currentUserId={userId} tr={tr} lang={lang}
                  onOpen={circle => openSheet(circle, 'scores')}/>
              </div>
            ))}
          </>
        )}

        {/* ── How it works ── */}
        <div style={{ marginTop: hasAnyContent ? '1.5rem' : 0 }}>
          <HowItWorks tr={tr}/>
        </div>

        {/* ── CTAs ── */}
        <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem', marginBottom:'0.6rem' }}>
          {/* Create private circle */}
          <button onClick={() => setShowSheet('create')}
            style={{ width:'100%', padding:'0.85rem', background:'transparent', border:`1px solid ${G.goldBdr}`, borderRadius:10, color:G.gold, fontFamily:G.sans, fontSize:'0.85rem', fontWeight:600, cursor:'pointer', letterSpacing:'0.03em' }}>
            ✦ {fr ? 'Créer un cercle privé' : 'Create a private circle'}
          </button>

          {/* Join waitlist */}
          {!onWaitlist ? (
            <button onClick={() => setShowSheet('join')}
              style={{ width:'100%', padding:'0.85rem', background:G.gold, border:'none', borderRadius:10, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.85rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.05em' }}>
              {tr('scout.join_cta')} ✦
            </button>
          ) : (
            <div style={{ textAlign:'center', padding:'0.85rem', background:G.goldPale, border:`1px solid ${G.goldBdr}`, borderRadius:10 }}>
              <div style={{ fontSize:'0.82rem', color:G.gold, fontWeight:600 }}>{tr('scout.joined')}</div>
              <div style={{ fontSize:'0.65rem', color:G.muted, marginTop:'0.2rem' }}>
                {fr ? 'Tu recevras une proposition dès qu\'un groupe est formé' : 'You\'ll receive a proposal once a group is formed'}
              </div>
            </div>
          )}
        </div>

        <div style={{ textAlign:'center', fontSize:'0.65rem', color:G.muted }}>
          {tr('scout.free_honey_gold')}
        </div>
      </div>

      {/* ── RSVP date picker sheet ── */}
      {rsvpInvite && (
        <RsvpSheet
          invite={rsvpInvite}
          lang={lang}
          onSubmit={handleRsvpSubmit}
          onClose={() => setRsvpInvite(null)}
        />
      )}

      {/* ── Create circle sheet ── */}
      {showSheet === 'create' && (
        <CreateScoutSheet
          onClose={() => setShowSheet(null)}
          onCreate={(scout) => {
            setShowSheet(null);
            setMyHostedScouts(prev => [{ ...scout, invitations: [] }, ...prev]);
          }}
        />
      )}

      {/* ── Proposal / Vote sheets ── */}
      {showSheet && openCircle && (
        <div onClick={() => setShowSheet(null)} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:G.bg, border:`1px solid ${G.border}`, maxWidth:480, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)', maxHeight:'92vh', overflowY:'auto' }}>
            {showSheet === 'proposal' && (
              <ProposalSheet circle={openCircle} currentUserId={userId} tr={tr} lang={lang}
                onConfirm={handleConfirm} onDecline={handleDecline}
                onClose={() => setShowSheet(null)}/>
            )}
            {showSheet === 'vote' && (
              <PostCircleSheet circle={openCircle} currentUserId={userId} tr={tr}
                onVote={handleVote} onClose={() => setShowSheet(null)}/>
            )}
          </div>
        </div>
      )}

      {/* ── Join waitlist sheet ── */}
      {showSheet === 'join' && (
        <div onClick={() => setShowSheet(null)} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:G.bg, border:`1px solid ${G.border}`, maxWidth:480, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)' }}>
            <JoinWaitlistSheet tr={tr} onJoin={handleJoinWaitlist} onClose={() => setShowSheet(null)}/>
          </div>
        </div>
      )}
    </div>
  );
}
